import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';
import SellerCard from '../components/SellerCard';
import toast from 'react-hot-toast';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

export default function FreeDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [error, setError] = useState('');
  const [showMessage, setShowMessage] = useState(false);
  const [messageBody, setMessageBody] = useState('');
  const [messageSending, setMessageSending] = useState(false);
  const [showDeliveryOptions, setShowDeliveryOptions] = useState(false);
  const [deliveryMethod, setDeliveryMethod] = useState('');
  const [claimProcessing, setClaimProcessing] = useState(false);

  useEffect(() => {
    const fetchListing = async () => {
      try {
        const { data } = await API.get(`/listings/${id}`);
        setListing(data.listing);
      } catch (err) {
        setError('Item not found');
      } finally {
        setLoading(false);
      }
    };
    fetchListing();
  }, [id]);

  const handleClaimClick = () => {
    if (!user) {
      navigate(`/login?redirect=/free/${id}`);
      return;
    }
    setShowDeliveryOptions(true);
  };

  const executeClaimItem = async () => {
    setClaimProcessing(true);
    try {
      const { data } = await API.post('/free/claim', {
        listingId: parseInt(id),
        deliveryMethod: deliveryMethod
      });

      toast.success(`Item claimed! ${deliveryMethod === 'pickup' ? 'Coordinate pickup with the giver.' : 'Arrange shipping details.'}`);
      setShowDeliveryOptions(false);
      setDeliveryMethod('');
      setTimeout(() => {
        navigate('/dashboard/free');
      }, 1500);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to claim item');
    } finally {
      setClaimProcessing(false);
    }
  };

  const handleSendMessage = async () => {
    if (!messageBody.trim() || !user) return;

    setMessageSending(true);
    try {
      await API.post('/messages', {
        recipientId: listing.seller_id,
        listingId: id,
        body: messageBody
      });

      toast.success('Message sent!');
      setMessageBody('');
      setShowMessage(false);
    } catch (err) {
      toast.error('Failed to send message');
    } finally {
      setMessageSending(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }} className="float-anim">🍒</div>
          <div style={{ fontSize: 14, color: 'var(--text-soft)' }}>Loading...</div>
        </div>
      </div>
    );
  }

  if (error || !listing) {
    return (
      <div style={{ maxWidth: 700, margin: '0 auto', padding: '40px 48px' }}>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 24, marginBottom: 12 }}>
            Item not found
          </h2>
          <button className="btn btn-cherry" onClick={() => navigate('/free')}>
            ← Back to Free Items
          </button>
        </div>
      </div>
    );
  }

  const images = listing.images?.length ? listing.images : [{ url: null }];

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '40px 48px 80px' }}>
      <button
        className="btn btn-ghost btn-sm"
        onClick={() => navigate('/free')}
        style={{ marginBottom: 24 }}
      >
        ← Back to Free Items
      </button>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 48, alignItems: 'start' }}>
        {/* Images */}
        <div>
          <div
            style={{
              borderRadius: 16,
              overflow: 'hidden',
              marginBottom: 16,
              aspectRatio: '1',
              background: 'var(--cream)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {images[0]?.url ? (
              <img
                src={`${API_BASE}${images[0].url}`}
                alt={listing.title}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <span style={{ fontSize: 80, opacity: 0.3 }}>📦</span>
            )}
          </div>

          {images.length > 1 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {images.slice(1).map((img, i) => (
                <div
                  key={i}
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: 10,
                    overflow: 'hidden',
                    background: 'var(--cream)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {img.url ? (
                    <img src={`${API_BASE}${img.url}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <span style={{ fontSize: 32, opacity: 0.3 }}>📦</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Details & Claim */}
        <div>
          <div style={{ marginBottom: 32 }}>
            <div
              style={{
                display: 'inline-block',
                background: '#4CAF50',
                color: 'white',
                padding: '6px 12px',
                borderRadius: 20,
                fontSize: 11,
                fontWeight: 700,
                marginBottom: 12
              }}
            >
              🆓 FREE
            </div>

            <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 28, fontWeight: 700, marginBottom: 12 }}>
              {listing.title}
            </h1>

            <p style={{ fontSize: 15, color: 'var(--text-mid)', lineHeight: 1.7, marginBottom: 24 }}>
              {listing.description}
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
              {listing.condition && (
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text-soft)', fontWeight: 700, marginBottom: 4 }}>Condition</div>
                  <div style={{ fontWeight: 600 }}>{listing.condition}</div>
                </div>
              )}
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-soft)', fontWeight: 700, marginBottom: 4 }}>Location</div>
                <div style={{ fontWeight: 600 }}>📍 {listing.location_city}, {listing.location_state}</div>
              </div>
            </div>
          </div>

          {/* Enhanced Giver Card with Reputation */}
          {listing.seller_id && (
            <SellerCard
              userId={listing.seller_id}
              onMessage={() => setShowMessage(true)}
            />
          )}

          {/* Claim Section */}
          {user && user.id !== listing.seller_id ? (
            <div style={{ background: '#E8F5E9', border: '2px solid #4CAF50', borderRadius: 14, padding: 20 }}>
              <h3 style={{ fontWeight: 700, fontSize: 16, marginBottom: 16 }}>🆓 Claim This Item (FREE)</h3>
              <p style={{ fontSize: 13, color: 'var(--text-mid)', marginBottom: 12 }}>
                No fees to claim. The giver will ship free, but if you choose shipping, you pay the shipping cost.
              </p>
              <p style={{ fontSize: 13, color: 'var(--text-mid)', marginBottom: 16 }}>
                <strong>Available:</strong> {listing.delivery_options === 'both' ? 'Pickup & Shipping' : listing.delivery_options === 'pickup' ? 'Local Pickup Only' : 'Shipping Only'}
              </p>

              <button
                className="btn btn-full"
                style={{ background: '#4CAF50', color: 'white', border: 'none' }}
                onClick={handleClaimClick}
                disabled={claimProcessing}
              >
                {claimProcessing ? 'Processing...' : '✓ Claim This Item'}
              </button>
            </div>
          ) : user && user.id === listing.seller_id ? (
            <div style={{ background: '#E8F5E9', borderRadius: 14, padding: 20, textAlign: 'center' }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>🆓</div>
              <p style={{ fontWeight: 700, fontSize: 14 }}>This is your item</p>
              <p style={{ fontSize: 13, color: 'var(--text-soft)' }}>You'll see claims here</p>
            </div>
          ) : (
            <div style={{ background: '#E3F2FD', border: '2px solid #1976D2', borderRadius: 14, padding: 20, textAlign: 'center' }}>
              <p style={{ fontSize: 14, color: 'var(--text-mid)', marginBottom: 16 }}>
                🆓 Free to claim - Sign up for a free account to get started
              </p>
              <button
                className="btn btn-full"
                style={{ background: '#1976D2', color: 'white', border: 'none' }}
                onClick={() => navigate(`/login?redirect=/free/${id}`)}
              >
                Sign In or Create Account
              </button>
            </div>
          )}
        </div>
      </div>


      {/* Delivery Options Modal */}
      {showDeliveryOptions && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowDeliveryOptions(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: 16,
              padding: 32,
              maxWidth: 600,
              width: '90%',
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
              <button
                onClick={() => setShowDeliveryOptions(false)}
                style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', padding: 0 }}
              >
                ←
              </button>
              <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 24, fontWeight: 700, margin: 0 }}>
                How do you want it?
              </h2>
            </div>

            <div style={{ display: 'grid', gap: 16, marginBottom: 24 }}>
              {/* Option 1: Pickup (if allowed) */}
              {(listing?.delivery_options === 'both' || listing?.delivery_options === 'pickup') && (
                <div
                  style={{
                    border: deliveryMethod === 'pickup' ? '2px solid #4CAF50' : '2px solid var(--border)',
                    borderRadius: 12,
                    padding: 20,
                    cursor: 'pointer',
                    background: deliveryMethod === 'pickup' ? '#F1F8E9' : 'white'
                  }}
                  onClick={() => setDeliveryMethod('pickup')}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ fontSize: 24 }}>📍</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>
                        Local Pickup
                      </div>
                      <p style={{ fontSize: 13, color: 'var(--text-mid)', margin: 0, lineHeight: 1.6 }}>
                        {listing?.suggested_pickup_city ? `Giver suggests ${listing.suggested_pickup_city}, ${listing.suggested_pickup_state}. You can propose another location.` : 'Arrange a safe public location with the giver.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Option 2: Ship (if allowed) */}
              {(listing?.delivery_options === 'both' || listing?.delivery_options === 'shipping') && (
                <div
                  style={{
                    border: deliveryMethod === 'shipping' ? '2px solid #FF9800' : '2px solid var(--border)',
                    borderRadius: 12,
                    padding: 20,
                    cursor: 'pointer',
                    background: deliveryMethod === 'shipping' ? '#FFFBF0' : 'white'
                  }}
                  onClick={() => setDeliveryMethod('shipping')}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ fontSize: 24 }}>📦</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>
                        Ship to Me
                      </div>
                      <p style={{ fontSize: 13, color: 'var(--text-mid)', margin: 0, lineHeight: 1.6 }}>
                        Giver ships free. You pay for shipping. Works great for items that are easy to pack.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={() => {
                if (!deliveryMethod) {
                  toast.error('Please select a delivery method');
                  return;
                }
                setShowDeliveryOptions(false);
                executeClaimItem();
              }}
              disabled={!deliveryMethod || claimProcessing}
              style={{
                width: '100%',
                padding: 14,
                background: deliveryMethod && !claimProcessing ? '#4CAF50' : 'var(--border)',
                color: 'white',
                border: 'none',
                borderRadius: 10,
                fontWeight: 700,
                cursor: deliveryMethod && !claimProcessing ? 'pointer' : 'not-allowed',
                marginBottom: 12,
                opacity: deliveryMethod && !claimProcessing ? 1 : 0.5
              }}
            >
              {claimProcessing ? '🔄 Claiming...' : '✓ Claim Item'}
            </button>

            <button
              onClick={() => setShowDeliveryOptions(false)}
              style={{
                width: '100%',
                padding: 12,
                background: 'var(--border)',
                border: 'none',
                borderRadius: 10,
                fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}


      {/* Message Modal */}
      {showMessage && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowMessage(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: 16,
              padding: 24,
              maxWidth: 500,
              width: '90%',
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, fontWeight: 700 }}>
                Message {listing.first_name}
              </h2>
              <button
                onClick={() => setShowMessage(false)}
                style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>

            <p style={{ fontSize: 13, color: 'var(--text-soft)', marginBottom: 16 }}>
              About: {listing.title}
            </p>

            <textarea
              placeholder="Ask about the item, suggest meeting times, or negotiate..."
              value={messageBody}
              onChange={e => setMessageBody(e.target.value)}
              style={{
                width: '100%',
                padding: 12,
                border: '1px solid var(--border)',
                borderRadius: 10,
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 13,
                minHeight: 120,
                marginBottom: 16,
                resize: 'none'
              }}
            />

            <button
              onClick={handleSendMessage}
              disabled={!messageBody.trim() || messageSending}
              style={{
                width: '100%',
                padding: 12,
                background: '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: 10,
                fontWeight: 700,
                cursor: messageBody.trim() ? 'pointer' : 'not-allowed',
                opacity: messageBody.trim() ? 1 : 0.5
              }}
            >
              {messageSending ? 'Sending...' : '💬 Send Message'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
